import {
  TCity,
  TFilterCriteria,
  TUser,
} from './types/counselor-grid-service-types';
import { ESortByOptionValue } from './types/counselor-grid-service-sort-option-types';
import { getUserDistance } from './counselor-grid-user-service';
import { getMetersFromDistance } from 'Main/utils/distance';

export function filterByCompetenceAreas(
  user: TUser,
  filteredCompetenceAreas: string[],
  type: string
): boolean {
  if (!user.competence_areas_list) {
    return false;
  }

  const userCompetences = user.competence_areas_list
    .filter((comp: { type: string }) => comp.type.toLowerCase() === type)
    .map((a: { name: string }) => a.name);

  return filteredCompetenceAreas.every((filteredCompetence: string) =>
    userCompetences.includes(filteredCompetence)
  );
}

export function filterByGender(
  user: TUser,
  gender: string | null,
  type: string
): boolean {
  if (!user.competence_areas_list) {
    return false;
  }

  if (!gender) {
    return true;
  }

  const userCompetences = user.competence_areas_list
    .filter((comp: { type: string }) => comp.type.toLowerCase() === type)
    .map((a: { name: string }) => a.name);

  return userCompetences.includes(gender);
}

export function filterUsersByMeetingType(
  users: TUser[],
  meetingTypes: string[]
): TUser[] {
  return users.filter((user) =>
    meetingTypes.some((meetingType) =>
      user.meeting_types?.includes(meetingType)
    )
  );
}

export function filterUsersByYearsOfExperience(
  users: TUser[],
  yearsOfExperience: number
): TUser[] {
  return users.filter((user) => {
    if (!user.years_of_experience) {
      return false;
    }

    return user.years_of_experience >= yearsOfExperience;
  });
}

export function filterUsersByAvailableTimes(
  users: TUser[],
  availableTimes: string[]
): TUser[] {
  return users.filter((user) => {
    if (!user.available_times) {
      return false;
    }

    return availableTimes.some(
      (availableTime) =>
        user.available_times &&
        Object.prototype.hasOwnProperty.call(
          user.available_times,
          availableTime
        )
    );
  });
}

export function filterUsersByLocation(
  users: TUser[],
  filteredLocation: string,
  role: string,
  cities: TCity[]
): TUser[] {
  let locationType = '';
  switch (role) {
    case 'lawyer':
      locationType = 'LawyerOrder';
      break;
    default:
      locationType = 'Order';
  }

  return users.filter((user) => {
    if (!user.locations_list) {
      return false;
    }

    const userLocations = user.locations_list
      .filter((location: { type: string }) => location.type === locationType)
      .map((a: { name: string }) => a.name);

    // Direct match - user has the exact location we're filtering for
    if (userLocations.includes(filteredLocation)) {
      return true;
    }

    if (cities) {
      for (const userLocation of userLocations) {
        const cityWithMatchingParent = cities.find(
          (city) => city.parent === userLocation
        );
        if (
          cityWithMatchingParent &&
          cityWithMatchingParent.label === filteredLocation
        ) {
          return true;
        }
      }
    }

    return false;
  });
}

export function filterUsersByAllowedDistance(
  users: TUser[],
  allowedDistance: number,
  currentSite: string
): TUser[] {
  return users.filter((user) => {
    if (!user.distance) {
      return false;
    }

    const meters = getMetersFromDistance(allowedDistance, currentSite);

    return user.distance <= meters;
  });
}

export async function filterAndSortUsersByCriteria(
  users: TUser[],
  filters: TFilterCriteria,
  role: string,
  cities: TCity[],
  sortByOption: ESortByOptionValue,
  googleMapsIsAvailable: boolean,
  currentSite: string
): Promise<{
  matchingUsers: TUser[];
  partiallyMatchingUsers: TUser[];
  notMatchingUsers: TUser[];
}> {
  if (!users) {
    return {
      matchingUsers: [],
      partiallyMatchingUsers: [],
      notMatchingUsers: [],
    };
  }

  // Manipulate users to include distance if a place is set
  // Split up sorted users into matchingUsers and notMatchingUsers based on filters
  const hasPlaceFilter = filters.place?.location;

  let matchingUsers = await Promise.all(
    users.map(async (user) => {
      user.distance = undefined;

      if (!hasPlaceFilter) {
        return user;
      }

      try {
        user.distance = await getUserDistance(
          user,
          filters.place,
          googleMapsIsAvailable
        );
      } catch (error) {
        console.error('Error getting user distance:', error);
      }

      return user;
    })
  );

  let partiallyMatchingUsers: TUser[] = [];
  let notMatchingUsers: TUser[] = [];

  const isTherapy = role === 'therapy';
  const isLawyer = role === 'lawyer';

  if ((isTherapy || isLawyer) && filters.competenceAreas.length > 0) {
    const type = isLawyer ? 'legal' : role;

    matchingUsers = matchingUsers.filter((user) =>
      filterByCompetenceAreas(user, filters.competenceAreas, type)
    );
  }

  if (isTherapy) {
    if (filters.accreditations.length > 0) {
      matchingUsers = matchingUsers.filter((user) =>
        filterByCompetenceAreas(user, filters.accreditations, role)
      );
    }

    if (filters.targetGroups.length > 0) {
      matchingUsers = matchingUsers.filter((user) =>
        filterByCompetenceAreas(user, filters.targetGroups, role)
      );
    }

    if (filters.titles.length > 0) {
      matchingUsers = matchingUsers.filter((user) =>
        filterByCompetenceAreas(user, filters.titles, role)
      );
    }

    if (filters.gender) {
      matchingUsers = matchingUsers.filter((user) =>
        filterByGender(user, filters.gender, role)
      );
    }
  }

  if (filters.languages.length > 0) {
    matchingUsers = matchingUsers.filter((user) => {
      if (!user.languages_list) {
        return false;
      }
      return filters.languages.every((filteredLanguage: string) =>
        user.languages_list!.includes(filteredLanguage)
      );
    });
  }

  if (!isTherapy && filters.location) {
    matchingUsers = filterUsersByLocation(
      matchingUsers,
      filters.location,
      role,
      cities
    );
  }

  if (filters.meetingTypes.length > 0) {
    matchingUsers = filterUsersByMeetingType(
      matchingUsers,
      filters.meetingTypes
    );
  }

  if (filters.yearsOfExperience) {
    matchingUsers = filterUsersByYearsOfExperience(
      matchingUsers,
      filters.yearsOfExperience
    );
  }

  if (filters.availableTimes.length > 0) {
    matchingUsers = filterUsersByAvailableTimes(
      matchingUsers,
      filters.availableTimes
    );
  }

  if (filters.allowedDistance) {
    const matchingUsersWithDistance = filterUsersByAllowedDistance(
      matchingUsers,
      filters.allowedDistance,
      currentSite
    );
    partiallyMatchingUsers = matchingUsers.filter(
      (user) => !matchingUsersWithDistance.includes(user)
    );
    matchingUsers = matchingUsersWithDistance;
  }

  notMatchingUsers = users.filter(
    (user) =>
      !(matchingUsers.includes(user) || partiallyMatchingUsers.includes(user))
  );

  return sortUsers(
    sortByOption,
    matchingUsers,
    partiallyMatchingUsers,
    notMatchingUsers
  );
}

function sortUsers(
  sortByOption: ESortByOptionValue,
  matchingUsers: TUser[],
  partiallyMatchingUsers: TUser[],
  notMatchingUsers: TUser[]
): {
  matchingUsers: TUser[];
  partiallyMatchingUsers: TUser[];
  notMatchingUsers: TUser[];
} {
  if (sortByOption === ESortByOptionValue.Default) {
    return { matchingUsers, partiallyMatchingUsers, notMatchingUsers };
  }

  if (sortByOption === ESortByOptionValue.Distance) {
    matchingUsers = sortUsersByDistance(matchingUsers);
    partiallyMatchingUsers = sortUsersByDistance(partiallyMatchingUsers);
    notMatchingUsers = sortUsersByDistance(notMatchingUsers);
  }

  if (sortByOption === ESortByOptionValue.Rating) {
    matchingUsers = sortUsersByRating(matchingUsers);
    partiallyMatchingUsers = sortUsersByRating(partiallyMatchingUsers);
    notMatchingUsers = sortUsersByRating(notMatchingUsers);
  }

  if (
    sortByOption === ESortByOptionValue.PriceDesc ||
    sortByOption === ESortByOptionValue.PriceAsc
  ) {
    matchingUsers = sortUsersByPrice(matchingUsers, sortByOption);
    partiallyMatchingUsers = sortUsersByPrice(
      partiallyMatchingUsers,
      sortByOption
    );
    notMatchingUsers = sortUsersByPrice(notMatchingUsers, sortByOption);
  }

  return { matchingUsers, partiallyMatchingUsers, notMatchingUsers };
}

function sortUsersByDistance(users: TUser[]): TUser[] {
  return users.sort((a: TUser, b: TUser) =>
    sortPropertyByNumericValue(a, b, 'distance', true)
  );
}

function sortUsersByRating(users: TUser[]): TUser[] {
  // Primary sorting by `rating`, secondary sorting by `number_of_reviews`
  return users.sort(
    (a: TUser, b: TUser) =>
      sortPropertyByNumericValue(a, b, 'rating', false) ||
      sortPropertyByNumericValue(a, b, 'number_of_reviews', false)
  );
}

function sortUsersByPrice(
  users: TUser[],
  sortByOption: ESortByOptionValue
): TUser[] {
  return users.sort((a: TUser, b: TUser) =>
    sortPropertyByNumericValue(
      a,
      b,
      'price_from',
      sortByOption === ESortByOptionValue.PriceAsc
    )
  );
}

function sortPropertyByNumericValue(
  a: TUser,
  b: TUser,
  property: keyof TUser,
  sortAscending: boolean
): number {
  const valueA = convertStringToSortableNumeric(a, property);
  const valueB = convertStringToSortableNumeric(b, property);

  if (!valueA && !valueB) {
    return 0;
  }

  if (!valueA || isNaN(valueA)) {
    return 1;
  }

  if (!valueB || isNaN(valueB)) {
    return -1;
  }

  return sortAscending ? valueA - valueB : valueB - valueA;
}

function convertStringToSortableNumeric(
  user: TUser,
  property: keyof TUser
): number {
  const value = user[property];

  if (!value) {
    return 0;
  }

  return (
    parseFloat(
      value
        .toString()
        .replace(',', '.')
        .replace(/[^\d.-]/g, '')
    ) || 0
  );
}
